import React from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useConfirm } from "material-ui-confirm";
import { Auth } from "aws-amplify";

import LogOutHeaderIcon from "../../icons/LogOutHeaderIcon";
import { isLocalEnvironment } from "../../../helpers/userLinkHelper";
import { RouteEnum, AuthPaths } from "../../../models/enums";
import CircularLoading from "../../progress/CircularLoading";
import confirmDialogStyleOptions from "../../../helpers/confirmDialogParams";
import { AuthApiClient } from "../../../api/AuthApiClient";
import { AuthUser } from "../../../types/user";
import { getIdTokenData } from "../../../utils/getJwtToken";

const MemoizedUserMenu = () => {
  const authClient = new AuthApiClient();

  const [userInfo, setUserInfo] = useState<AuthUser>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    async function getUserInfo() {
      const userInfo = await authClient.getCurentUser();

      setUserInfo(userInfo);
    }

    getUserInfo();
  }, []);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const confirm = useConfirm();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    confirm({
      title: "Are you sure you want to log out?",
      confirmationText: "Confirm",
      cancellationText: "Cancel",
      ...confirmDialogStyleOptions,
    })
      .then(async () => {
        const idTokenData = await getIdTokenData();

        if (idTokenData?.at_hash || isLocalEnvironment()) {
          await Auth.signOut();
        } else {
          await Auth.signOut({ global: true });
        }

        Cookies.remove("amplify_config");

        localStorage.clear();

        const redirectUrl = isLocalEnvironment()
          ? RouteEnum.Login
          : AuthPaths.Administration;

        window.location.replace(redirectUrl);
      })
      .catch(() => {
        handleClose();
      });
  };

  return (
    <Box>
      <IconButton
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <Avatar
          sx={{
            backgroundColor: theme =>
              open
                ? theme.palette.primary.shades?.["8p"]
                : theme.palette.otherOutlineBorder.main,
            color: theme => theme.palette.text.secondary,
          }}
        >
          <Typography variant="body2Bold">
            <>
              {userInfo?.first_name?.[0] ?? "U"}
              {userInfo?.last_name?.[0] ?? "U"}
            </>
          </Typography>
        </Avatar>
      </IconButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        elevation={0}
        sx={{
          p: 0,
          minWidth: "244px",
          borderRadius: "8px",
          ".MuiList-padding": {
            p: 0,
          },
          ".MuiPaper-root": {
            boxShadow: "none",
            borderRadius: "8px",
            border: theme =>
              `1px solid ${theme.palette.otherOutlineBorder.main}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: "244px",
            px: "1em",
            py: "0.5em",
          }}
        >
          {userInfo ? (
            <>
              <Typography variant="body2Bold">
                {userInfo.first_name} {userInfo.last_name}
              </Typography>

              <Typography sx={{ fontSize: "12px" }}>
                {userInfo.email}
              </Typography>
            </>
          ) : (
            <CircularLoading />
          )}
        </Box>

        <Divider />

        <MenuItem
          sx={{
            height: "44px",
            "&:hover": {
              backgroundColor: theme => theme.palette.primary.shades?.["8p"],
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                backgroundColor: theme => theme.palette.primary.main,
              },
            },
          }}
          onClick={handleSignOut}
        >
          <ListItemIcon>
            <LogOutHeaderIcon />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
const UserMenu = React.memo(MemoizedUserMenu);

export default UserMenu;
